import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { KeyHint } from './KeyHint'
import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface BaseProps {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  /* Left-side icon; keep it 18–20px. */
  icon?: ReactNode
  /* Keyboard shortcut shown as a round badge after the label. */
  kbd?: string
  children: ReactNode
  className?: string
}

interface AsButton extends BaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> {
  href?: undefined
}

interface AsLink extends BaseProps {
  href: string
  /* Full page load instead of an Inertia visit (external URLs, downloads). */
  external?: boolean
  target?: string
  rel?: string
}

export type ButtonProps = AsButton | AsLink

function classes({ variant = 'secondary', size = 'md', fullWidth, className }: BaseProps) {
  return [styles.btn, styles[variant], styles[size], fullWidth && styles.fullWidth, className]
    .filter(Boolean)
    .join(' ')
}

function Inner({ icon, kbd, children }: Pick<BaseProps, 'icon' | 'kbd' | 'children'>) {
  return (
    <>
      {icon && <span className={styles.icon}>{icon}</span>}
      {children}
      {kbd && <KeyHint keyName={kbd} />}
    </>
  )
}

export function Button(props: ButtonProps) {
  if (props.href !== undefined) {
    const { href, external, target, rel, variant, size, fullWidth, icon, kbd, children, className } = props
    const cls = classes({ variant, size, fullWidth, className, children })
    const inner = <Inner icon={icon} kbd={kbd}>{children}</Inner>
    return external || target ? (
      <a href={href} target={target} rel={rel} className={cls}>{inner}</a>
    ) : (
      <Link href={href} className={cls}>{inner}</Link>
    )
  }

  const { variant, size, fullWidth, icon, kbd, children, className, type = 'button', ...rest } = props
  return (
    <button type={type} className={classes({ variant, size, fullWidth, className, children })} {...rest}>
      <Inner icon={icon} kbd={kbd}>{children}</Inner>
    </button>
  )
}
